/**
 * @file AddressInput.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Thursday, 24th October 2019 7:38:43 pm
 * @copyright 2015 - 2019 SKAT LLC, Delive LLC
 */
import type { ButtonModel, CombinedValue } from 'web-panel/types'

import * as React from 'react'
import Autocomplete from './Autocomplete'
import ReferenceSelect from './ReferenceSelect'
import Select from './Select'

import cn from 'classnames'
import { __, UNSET } from '../../../globals'
import FieldIcon from './FieldIcon'
import { CAST, KEY } from 'web-panel-essentials/misc'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faMapMarkerAlt, faPlus, faMinus, faGlobe } from '@fortawesome/free-solid-svg-icons'
import { STUBProvider } from 'web-panel/connections/DataProvider'
import { Inject } from 'web-panel/serviceLocator'

import autoBind from 'react-autobind'

library.add(faMapMarkerAlt, faPlus, faMinus, faGlobe)

class AddressInput extends React.Component {
  @Inject cityWizard : {}

  constructor (props) {
    super(props)
    autoBind(this)

    this.state = {
      focused: this.props.catchFocus || false,
      disabled: props.disabled
    }

    this.cityProvider = props.cityProvider || this.STUBProvider
    this.cityDataManager = props.cityDataManager
    this.streetProvider = props.streetProvider || this.STUBProvider
    this.houseProvider = props.houseProvider || this.STUBProvider
    this.regionProvider = props.regionProvider || this.STUBProvider
  }

  updateIfNecessary (props, attr) {
    if (props[attr] === undefined) return false
    if (props[attr] !== this.state[attr]) {
      const changes = {}
      changes[attr] = props[attr]
      this.setState(changes)
      return true
    }
  }

  get STUBProvider () {
    return STUBProvider
  }

  handleKeyDown (e) {
    const shift = this.props.onPositionShift
    if (!shift) return
    if (e.altKey) {
      if (e.keyCode === KEY.DOWN) shift(this.props.position, 'down')
      if (e.keyCode === KEY.UP) shift(this.props.position, 'up')
    }
  }

  handleFocus (e) {
    this.setState({ focused: true })
    this.props.onFocus && this.props.onFocus(e)
  }

  handleBlur (e) {
    this.setState({ focused: false })
    this.props.onBlur && this.props.onBlur(e)
  }

  handleSubFieldBlur (fieldName) {
    this.props.onSubFieldBlur && this.props.onSubFieldBlur(fieldName)
  }

  async handleToggleCity (e) {
    const { onChange, cityId, defaultCityId, defaultCityName } = this.props
    if (onChange) {
      const hasCity: boolean = Boolean(cityId)

      if (hasCity) onChange('cityId', { value: null, displayValue: null })
      else onChange('cityId', { value: defaultCityId, displayValue: defaultCityName })

      this.handleSubFieldBlur('cityId')
    }
  }

  async handleCityRecordsChange (records) {
    const [record] = records
    if (record) {
      this.props.onChange('cityId', { value: record.id, displayValue: record.name })
      this.handleSubFieldBlur('cityId')
    }
  }

  handleCityChange (changes: CombinedValue) : void {
    if (!changes.value && !changes.displayValue) {
      // Hanlding only resets. Direct changes are overcomplicated
      this.props.onChange('cityId', changes)
    }
  }

  handleRegionChange ({ value, displayValue }) {
    if (this.props.onChange) {
      this.props.onChange('regionId', value)
      this.props.onChange('regionName', displayValue)
      this.props.onSubFieldBlur('regionId')
    }
  }

  handleStreetChange (value: CombinedValue) {
    if (this.props.onChange) this.props.onChange('street', value)
  }

  handleHouseChange ({ displayValue }) {
    if (this.props.onChange) this.props.onChange('house', displayValue)
  }

  handleApartmentChange (event) {
    const displayValue = event.target.value
    if (this.props.onChange) this.props.onChange('apartment', displayValue)
  }

  handleEntranceChange (event) {
    const displayValue = event.target.value
    if (this.props.onChange) this.props.onChange('entrance', displayValue)
  }

  handleFloorChange (event) {
    const displayValue = event.target.value
    if (this.props.onChange) this.props.onChange('floor', displayValue)
  }

  handleRemoveButtonPress = () => {
    const { onRemoveButtonPress, removalDenied } = this.props
    if (onRemoveButtonPress && !removalDenied) {
      onRemoveButtonPress(this.props.id)
    }
  }

  handleCityBlur (e) {
    this.handleBlur(e)
  }

  _renderCity () {
    const { cityName, cityId, noCity, _lockedFields = {} } = this.props
    if (noCity) return null

    const actions : Array<ButtonModel> = [{
      name: 'add-city',
      icon: 'globe',
      title: __('ADD_CITY'),
      label: __('ADD_CITY'),
      action: async () => this.cityWizard.invoke()
    }]

    return (
      <>
        <FieldIcon icon='globe' active={Boolean(cityId)} onClick={this.handleToggleCity} />
        <ReferenceSelect
          value={cityId}
          actions={actions}
          displayValue={cityName}
          onChange={this.handleCityChange}
          onFocus={this.handleFocus}
          onBlur={this.handleCityBlur}
          className={cn('city', { collapsed: !cityId })}
          disabled={!cityId}
          placeholder={__('CITY')}
          filter={{ where: { id: { '!=': UNSET.CITY.id } } }}
          readOnly={_lockedFields.city || _lockedFields.cityId}
          dataManager={this.cityDataManager}
          onRecordsChange={this.handleCityRecordsChange}
          dataProvider={this.cityProvider}
        />
      </>
    )
  }

  _renderRegion () {
    const { focused } = this.state
    const { regionId, regionName, regionsFilter, _regionDetected, _lockedFields = {}, noRegion } = this.props
    if (noRegion) return null

    return (
      <div className={cn('field', 'l3', 'field-address', { focused })}>
        <Select
          filter={regionsFilter}
          invalid={!_regionDetected}
          value={regionId}
          displayValue={regionName}
          className='district'
          placeholder={__('REGION')}
          onChange={this.handleRegionChange}
          readOnly={_lockedFields.region || _lockedFields.regionId}
          dataProvider={this.regionProvider}
        />
      </div>
    )
  }

  handleAddButtonPress = () => {
    this.props.onAddButtonPress(this.props.position)
  }

  _renderControls () {
    const { removalDenied, noControls, _lockedFields } = this.props
    if (noControls) return null

    return (
      <div className='address-controls field l4'>
        <FieldIcon icon='minus' disabled={removalDenied || _lockedFields.__route} onClick={this.handleRemoveButtonPress} />
        <FieldIcon icon='plus' disabled={_lockedFields.__route} onClick={this.handleAddButtonPress} />
      </div>
    )
  }

  handleMarkerClick = () => {
    const { id, latitude, longitude, street, house } = this.props
    this.props.onNavigate && this.props.onNavigate({ id, latitude, longitude, street, house })
  }

  handleHouseBlur = (e) => {
    this.handleBlur(e)
    this.handleSubFieldBlur('house')
  }

  handleStreetBlur = (e) => {
    this.handleBlur(e)
    this.handleSubFieldBlur('street')
  }

  render () : React.Node {
    const { focused } = this.state
    const { catchFocus, streetId, cityId, contextCityId, name, street, house, apartment, entrance, floor, latitude, longitude, _lockedFields = {}, noAppartment, noEntrance, noFloor } = this.props
    const { placeholder } = this.props

    let streetFilter
    let houseFilter
    let targetCityId

    if (contextCityId && CAST.Number(contextCityId) !== UNSET.CITY.id) targetCityId = contextCityId
    if (cityId && CAST.Number(cityId) !== UNSET.CITY.id) targetCityId = cityId

    if (street) houseFilter = { where: { street: { ilike: street } } }

    if (targetCityId) {
      streetFilter = { where: { cityId: targetCityId } }
      if (houseFilter) houseFilter = { ...houseFilter, cityId: targetCityId }
    }

    return (
      <div className='address-box'>
        {name ? <div className='tip'>{name}</div> : null}
        <div className={cn('field', 'l1', 'field-address', { focused })}>
          {this._renderCity()}

          <Autocomplete
            displayValue={street}
            value={streetId}
            onFocus={this.handleFocus}
            onKeyDown={this.handleKeyDown}
            onChange={this.handleStreetChange}
            readOnly={_lockedFields.street || _lockedFields.streetId}
            onBlur={this.handleStreetBlur}
            className='street'
            filter={streetFilter}
            focused={catchFocus}
            placeholder={placeholder || __('STREET')}
            dataProvider={this.streetProvider}
          />

          <Autocomplete
            displayValue={house}
            onFocus={this.handleFocus}
            onKeyDown={this.handleKeyDown}
            readOnly={_lockedFields.house}
            onBlur={this.handleHouseBlur}
            onChange={this.handleHouseChange}
            className='house'
            filter={houseFilter}
            placeholder={__('HOUSE')}
            dataProvider={this.houseProvider}
          />

          <FieldIcon className={cn({ positive: (latitude && longitude) })} icon='map-marker-alt' onClick={this.handleMarkerClick} />
        </div>

        {/*
        Здесь есть небольшой костыль связанный с вёрсткой: ПОЛЕ ENTRANCE НИКОГДА НЕ NULL, оно только скрывается, чтобы
        строка с адресом не схлопывалась
        */}
        <div className={cn('field', 'l2', 'field-address', { focused, 'hidden-visibility': noEntrance })}>

          {noAppartment
            ? null
            : (
              <input
                value={apartment}
                onKeyDown={this.handleKeyDown}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                onChange={this.handleApartmentChange}
                readOnly={_lockedFields.apartment}
                className='apartment'
                placeholder={__('APARTMENT')}
                type='text'
              />
            )}

          <input
            value={entrance}
            onKeyDown={this.handleKeyDown}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            readOnly={_lockedFields.entrance}
            onChange={this.handleEntranceChange}
            className={cn('entrance', { 'hidden-visibility': noEntrance })}
            placeholder={__('ENTRANCE')}
            type='text'
          />

          {noFloor
            ? null
            : (
              <input
                onKeyDown={this.handleKeyDown}
                value={floor}
                onFocus={this.handleFocus}
                onBlur={this.handleBlur}
                readOnly={_lockedFields.floor}
                onChange={this.handleFloorChange}
                className='floor'
                placeholder={__('FLOOR')}
                type='text'
              />
            )}
        </div>

        {this._renderRegion()}
        {this._renderControls()}
      </div>
    )
  }
}

export default AddressInput
